import {
  type ResourceEvent,
  type ViewerProps as ViewerPropsBase,
  type ViewerState as ViewerStateBase,
  Viewer as ViewerBase,
} from '@seddi/ui-three-viewer';
import { Button, Spinner } from '@seddi/ui-kit';
import log from 'loglevel';
import type { FC } from 'react';
import type { Dispatch } from 'redux';
import styled from 'styled-components';

import type { MockupLogo } from '../../types/mockupLogos';
import type { LogoRegion } from '../../viewer/viewerScene';
import { useAppViewer } from './AppViewer.hooks';
import { AppViewerTool } from './appViewerTool';
import { DecreaseIcon, IncreaseIcon } from '../../icons/ZoomIcons';
import type { ViewerIdleAnimationMode } from '../../pages/StyleViewerPage.hooks';

type ViewerProps = ViewerPropsBase & {
  dispatch: Dispatch;
  hoverGraphicId: string | null;
  idleAnimationMode?: ViewerIdleAnimationMode;
  logoRegions: LogoRegion[];
  logos: MockupLogo[];
  selectedGraphicId: string | null;
  showGraphicsPanel: boolean;
};

type ViewerState = ViewerStateBase & {
  // more
};

type ControlsLayout = 'tr' | 'tl' | 'bl' | 'br';

type ControlsStyle = {
  textColor?: string;
  backgroundColor?: string;
  borderColor?: string;
};

type AppViewerProps = {
  controlsLayout?: ControlsLayout;
  controlsStyle?: ControlsStyle;
  idleAnimationMode?: ViewerIdleAnimationMode;
  showGraphicsPanel?: boolean;
  useBrandLogos?: boolean;
};

export class Viewer extends ViewerBase<ViewerProps, ViewerState> {
  constructor(props: ViewerProps) {
    super(props);

    const handleResourceChangeBase = this.handleResourceChange;
    this.handleResourceChange = ((event: ResourceEvent) => {
      handleResourceChangeBase(event);

      if (event.status !== 'ready') return;

      const tool = this.tool as AppViewerTool;

      switch (event.kind) {
        case 'GarmentGeometry': {
          const garmentGeometry = this.resources.findGarmentGeometry(event.id);
          tool.updateGarmentGeometry(garmentGeometry);
          tool.updateLogos(this.props.logos);
          tool.updateSelection(this.props.selectedGraphicId);
          break;
        }
        case 'Model': {
          const model = this.resources.findModel(event.id);
          tool.updateModel(model);
          tool.updateLogos(this.props.logos);
          tool.updateSelection(this.props.selectedGraphicId);
          break;
        }
      }
    }) as EventListener;
  }

  componentDidMount() {
    super.componentDidMount();

    log.info('Scene is loading');

    const { camera, props } = this;
    camera.parent = null;

    const canvas = this.canvasRef.current!;
    const enableRaycast = props.showGraphicsPanel;
    const enableTransform = enableRaycast;

    this.tool = new AppViewerTool({
      camera,
      canvas,
      dispatch: props.dispatch,
      enableRaycast,
      enableTransform,
      idleAnimationMode: this.props.idleAnimationMode,
    });

    const tool = this.tool as AppViewerTool;
    tool.updateRegions(props.logoRegions);
    tool.updateLogos(props.logos);
    tool.updateSelection(props.selectedGraphicId);
    tool.updateHover(props.hoverGraphicId);
  }

  shouldComponentUpdate(nextProps: Readonly<ViewerProps>): boolean {
    const isUpdate = super.shouldComponentUpdate(nextProps);
    const { props } = this;

    if (
      props.hoverGraphicId !== nextProps.hoverGraphicId ||
      props.logoRegions !== nextProps.logoRegions ||
      props.logos !== nextProps.logos ||
      props.selectedGraphicId !== nextProps.selectedGraphicId ||
      props.showGraphicsPanel !== nextProps.showGraphicsPanel
    ) {
      return true;
    }

    return isUpdate;
  }

  componentDidUpdate(prevProps: ViewerProps) {
    super.componentDidUpdate(prevProps, this.state);

    const tool = this.tool as AppViewerTool;
    const { props } = this;

    if (props.logoRegions !== prevProps.logoRegions) {
      tool.updateRegions(props.logoRegions);
    }

    if (props.logos !== prevProps.logos) {
      tool.updateLogos(props.logos);
    }

    if (props.selectedGraphicId !== prevProps.selectedGraphicId) {
      tool.updateSelection(props.selectedGraphicId);
    }

    if (props.hoverGraphicId !== prevProps.hoverGraphicId) {
      tool.updateHover(props.hoverGraphicId);
    }
  }
}

const Container = styled.div`
  background-color: rgba(255, 255, 255, 0);
  display: block;
  flex: 1;
  height: 100%;
  position: relative;
  min-width: 0;
`;

const PreviewImage = styled.img`
  display: block;
  height: 100%;
  object-fit: contain;
  width: 100%;
  filter: blur(5px);
`;

const LoadingSpinner = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
`;

const MessageOverlay = styled.div`
  align-items: center;
  color: #333;
  display: flex;
  font-size: 1rem;
  height: 100%;
  justify-content: center;
  padding: 1rem;
  text-align: center;
  width: 100%;
`;

const ResetCameraButton = styled.div`
  width: 100px;
  cursor: pointer;

  button {
    width: 100%;
    color: ${({ theme }) => (theme as any).color.ui3};
  }
`;

const ZoomButtonGroup = styled.div`
  display: flex;
  width: 100px;
`;

const ZoomButton = styled.button<{ disabled?: boolean; $controlsStyle?: ControlsStyle }>`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  height: 32px;
  background-color: ${({ theme, $controlsStyle }) =>
    $controlsStyle?.backgroundColor ?? (theme as any).color.ui6};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  border: 1px solid
    ${({ theme, $controlsStyle }) => $controlsStyle?.borderColor ?? (theme as any).color.ui5};
  box-shadow: ${({ theme }) => (theme as any).shadows.outerShadow1};
  opacity: ${({ disabled }) => (disabled ? 0.4 : 1)};
  font-size: 14px;
  font-weight: 500;
  line-height: 1;
  ${({ theme }) => (theme as any).font.t3};
  color: ${({ theme, $controlsStyle }) => $controlsStyle?.textColor ?? (theme as any).color.ui3};
  box-sizing: border-box;

  svg {
    width: 16px;
    height: 16px;
    display: block;
  }

  &:first-child {
    border-top-left-radius: 20px;
    border-bottom-left-radius: 20px;
    border-right: none;
  }

  &:last-child {
    border-top-right-radius: 20px;
    border-bottom-right-radius: 20px;
  }

  &:hover {
    background-color: ${({ theme, disabled, $controlsStyle }) => {
      if ($controlsStyle?.backgroundColor) {
        return $controlsStyle.backgroundColor;
      }

      const t = theme as any;
      return disabled ? t.color.ui6 : t.color.ui4;
    }};
  }

  &:active {
    background-color: ${({ theme, disabled, $controlsStyle }) => {
      if ($controlsStyle?.backgroundColor) {
        return $controlsStyle.backgroundColor;
      }

      const t = theme as any;
      return disabled ? t.color.ui6 : t.color.ui3;
    }};
  }
`;

const ZoomControls = styled(ZoomButtonGroup)`
  width: 100px;
`;

const ControlsContainer = styled.div<{ layout: ControlsLayout }>`
  position: absolute;
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: stretch;

  ${({ layout }) => {
    switch (layout) {
      case 'tl':
        return 'top: 12px; left: 12px;';
      case 'tr':
        return 'top: 12px; right: 12px;';
      case 'bl':
        return 'bottom: 12px; left: 12px;';
      case 'br':
        return 'bottom: 12px; right: 12px;';
      default:
        return 'top: 12px; right: 12px;';
    }
  }}
`;

export const AppViewer: FC<AppViewerProps> = ({
  controlsLayout = 'tr',
  controlsStyle,
  idleAnimationMode = 'static',
  showGraphicsPanel = false,
  useBrandLogos = true,
}) => {
  const {
    config,
    dispatch,
    featureFlags,
    handleUpdate,
    handleCameraReset,
    handleZoomIn,
    handleZoomOut,
    hoverGraphicId,
    logoRegions,
    mockupLogos,
    scene,
    sceneIsLoading,
    selectedGraphicId,
    stylePreviewUrl,
    styleNotFoundMessage,
    viewerRef,
    visible,
    waitingForFirstFrame,
  } = useAppViewer(showGraphicsPanel, useBrandLogos);

  const resetButtonStyle = controlsStyle
    ? {
        color: controlsStyle.textColor,
        backgroundColor: controlsStyle.backgroundColor,
        borderColor: controlsStyle.borderColor,
      }
    : undefined;

  return (
    <Container>
      {(sceneIsLoading || waitingForFirstFrame) && stylePreviewUrl && (
        <PreviewImage src={stylePreviewUrl} alt="Style preview" />
      )}
      {(!scene || sceneIsLoading) && !styleNotFoundMessage && (
        <LoadingSpinner>
          <Spinner type="orbit" />
        </LoadingSpinner>
      )}
      {scene && (
        <>
          <Viewer
            key={showGraphicsPanel ? 'with-graphics' : 'no-graphics'}
            backgroundAlpha={0}
            backgroundColor="#000000"
            config={config}
            dispatch={dispatch}
            featureFlags={featureFlags}
            hoverGraphicId={hoverGraphicId ?? null}
            id="canvas"
            idleAnimationMode={idleAnimationMode}
            logoRegions={logoRegions}
            logos={mockupLogos}
            onUpdate={handleUpdate}
            preserveDrawingBuffer={true}
            ref={viewerRef}
            scene={scene}
            selectedGraphicId={selectedGraphicId ?? null}
            showGarmentGeometry={true}
            showGraphicsPanel={showGraphicsPanel}
            visible={visible}
          />
          <ControlsContainer layout={controlsLayout}>
            <ResetCameraButton onClick={handleCameraReset}>
              <Button
                text="Reset View"
                look="negative"
                onClick={handleCameraReset}
                style={resetButtonStyle}
              />
            </ResetCameraButton>
            <ZoomControls>
              <ZoomButton onClick={handleZoomOut} $controlsStyle={controlsStyle}>
                <DecreaseIcon />
              </ZoomButton>
              <ZoomButton onClick={handleZoomIn} $controlsStyle={controlsStyle}>
                <IncreaseIcon />
              </ZoomButton>
            </ZoomControls>
          </ControlsContainer>
        </>
      )}

      {!scene && styleNotFoundMessage && <MessageOverlay>{styleNotFoundMessage}</MessageOverlay>}
    </Container>
  );
};
